import type { FC } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { Flex, Image } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import 'swiper/css';
import 'swiper/css/pagination';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import type { SwiperRef } from 'swiper/react';
import { AssetType } from '../../../../components/photo-grid/utils/types';
import { useNavigateTo } from '../../../../hooks';
import { useBuildURL } from '../../../../hooks/useBuildURL';
import { Text } from '../../../../ui-components';
import { IconButton } from '../../../../ui-components/button/icon-button';
import { CarouselWrapper } from '../../../../ui-components/carousel/CarouselWrapper';
import { getGiftCardDesignName } from '../../../../utils/product-utils';
import styles from './DesignSelector.module.scss';
import type { Product } from '../../../../bff-api/products/model';

interface DesignSelectorProps {
    product?: Product;
}

export const DesignSelector: FC<DesignSelectorProps> = ({ product }) => {
    const { buildPdpURL } = useBuildURL();
    const navigateTo = useNavigateTo();

    const assets = product?.c_variants
        ?.filter((variant) => !variant.isOutOfStock)
        .map((variant) => {
            const designName = product.variationAttributes?.[0].values.find(
                (attribute) => attribute.value === variant.id,
            )?.name;

            return {
                id: variant.id,
                assetUrl: variant.c_imageUrl,
                assetType: AssetType.IMAGE,
                title: designName,
                alt: designName,
            };
        });

    const ref = useRef<SwiperRef>(null);

    const findSelectedDesignIndex = useCallback(
        (designId?: string) => assets?.findIndex((asset) => asset.id === (designId || product?.id)),
        [assets, product?.id],
    );

    const scrollToSelectedDesign = useCallback(
        (designId?: string) => {
            if (ref.current?.swiper) {
                const selectedIndex = findSelectedDesignIndex(designId);
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                (ref.current.swiper as SwiperType).slideTo(selectedIndex ?? 0);
            }
        },
        [findSelectedDesignIndex],
    );

    useEffect(() => {
        if (product?.id) {
            scrollToSelectedDesign(product.id);
        }
    }, []);

    const handleSwiperInit = useCallback(() => {
        scrollToSelectedDesign();
    }, [scrollToSelectedDesign]);

    const onClickSlide = (designId: string): void => {
        if (product && product.id !== designId) {
            navigateTo(buildPdpURL(product, designId));
            scrollToSelectedDesign(designId);
        }
    };

    const handleNavigateNext = (): void => {
        if (ref.current?.swiper) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            (ref.current.swiper as SwiperType).slideNext();
        }
    };

    const handleNavigatePrev = (): void => {
        if (ref.current?.swiper) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            (ref.current?.swiper as SwiperType)?.slidePrev();
        }
    };

    return (
        <Flex
            gap={4}
            direction="column"
            data-testid="design-selector"
            className={styles.giftCardDesignSelector}
        >
            <Flex justifyContent="space-between" gap={2} alignItems="end">
                <Flex gap={1} flexWrap="wrap">
                    <Text style="bold">
                        <FormattedMessage
                            id="gift_card.select_design"
                            defaultMessage="Select a Design"
                        />
                    </Text>
                    <Text>
                        <FormattedMessage
                            id="gift_card.select_design_name"
                            defaultMessage="{designName}"
                            values={{ designName: product ? getGiftCardDesignName(product) : '' }}
                        />
                    </Text>
                </Flex>
                <Flex gap={2}>
                    <IconButton
                        onClick={handleNavigatePrev}
                        size="md"
                        id="slider-navigation-left"
                        iconName="navigation-arrow-left"
                    />
                    <IconButton
                        onClick={handleNavigateNext}
                        size="md"
                        id="slider-navigation-right"
                        iconName="navigation-arrow-right"
                    />
                </Flex>
            </Flex>
            <CarouselWrapper>
                <Swiper
                    id="test"
                    ref={ref}
                    slidesPerView="auto"
                    slidesPerGroup={1}
                    spaceBetween={16}
                    rewind
                    style={{ width: '100%' }}
                    modules={[Navigation]}
                    lazyPreloadPrevNext={2}
                    navigation
                    onInit={handleSwiperInit}
                >
                    {assets?.map((asset) => (
                        <SwiperSlide key={asset.id}>
                            <button
                                className="relative block w-full"
                                type="button"
                                style={{
                                    borderWidth: '1px',
                                    borderColor:
                                        product?.id === asset.id
                                            ? 'var(--chakra-colors-slt-pepper-1000)'
                                            : 'var(--chakra-colors-slt-pepper-300)',
                                    borderRadius: '16px',
                                }}
                                onClick={() => onClickSlide(asset.id)}
                                data-testid={`gift-card-design-option-${asset.id}`}
                            >
                                <Image
                                    data-testid={asset.id}
                                    width="200px"
                                    height="132px"
                                    objectFit="cover"
                                    src={asset.assetUrl}
                                    alt={asset.alt}
                                    borderRadius="16px"
                                />
                            </button>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </CarouselWrapper>
        </Flex>
    );
};

DesignSelector.defaultProps = {
    product: undefined,
};
